import React, { useState, useEffect, useRef } from "react";

const Header = ({ user, onSignOut }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Close mobile menu after navigation
    setIsMobileMenuOpen(false);
  };

  const getUserInitials = (displayName, email) => {
    if (displayName) {
      return displayName
        .split(" ")
        .map((name) => name[0])
        .join("")
        .toUpperCase();
    }
    return email ? email[0].toUpperCase() : "U";
  };

  const getUserDisplayName = (displayName, email) => {
    return displayName || email?.split("@")[0] || "User";
  };

  const getUserEmail = (email) => {
    return email || "user@example.com";
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest(".hamburger-button")
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  return (
    <header className="header">
      <div className="container header-container">
        {/* Logo */}
        <div className="logo-container">
          <img
            src={`${process.env.PUBLIC_URL}/images/logo-playfast.svg`}
            alt="PlayFast Logo"
            className="header-logo-image"
          />
        </div>

        {/* Desktop Navigation */}
        <nav className="desktop-nav nav-container">
          <button
            onClick={() => scrollToSection("players")}
            className="nav-button"
          >
            Players
          </button>
          <button
            onClick={() => scrollToSection("playbooks")}
            className="nav-button"
          >
            Playbooks
          </button>
          <button
            onClick={() => scrollToSection("engagement")}
            className="nav-button"
          >
            Engagement
          </button>
        </nav>

        {/* Desktop Profile Dropdown */}
        <div className="desktop-profile profile-dropdown">
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="profile-button"
          >
            <div className="profile-avatar">
              {getUserInitials(user?.displayName, user?.email)}
            </div>
            {getUserDisplayName(user?.displayName, user?.email)}
            <span className="profile-dropdown-arrow">▼</span>
          </button>

          {isProfileOpen && (
            <div className="profile-menu">
              <div className="profile-menu-content">
                <button
                  onClick={() => {
                    setIsProfileOpen(false);
                    onSignOut && onSignOut();
                  }}
                  className="profile-menu-item"
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Hamburger Button */}
        <button
          className="hamburger-button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="mobile-menu-backdrop"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* Mobile Slide-out Menu */}
      <div
        ref={mobileMenuRef}
        className={`mobile-menu ${isMobileMenuOpen ? "mobile-menu-open" : ""}`}
      >
        <div className="mobile-menu-header">
          <div className="mobile-menu-user">
            <div className="mobile-menu-avatar">
              {getUserInitials(user?.displayName, user?.email)}
            </div>
            <div className="mobile-menu-user-info">
              <div className="mobile-menu-user-name">
                {getUserDisplayName(user?.displayName, user?.email)}
              </div>
              <div className="mobile-menu-user-email">
                {getUserEmail(user?.email)}
              </div>
            </div>
          </div>
        </div>

        <nav className="mobile-menu-nav">
          <button
            onClick={() => scrollToSection("players")}
            className="mobile-menu-item"
          >
            Players
          </button>
          <button
            onClick={() => scrollToSection("playbooks")}
            className="mobile-menu-item"
          >
            Playbooks
          </button>
          <button
            onClick={() => scrollToSection("engagement")}
            className="mobile-menu-item"
          >
            Engagement
          </button>
          <button
            onClick={() => {
              setIsMobileMenuOpen(false);
              onSignOut && onSignOut();
            }}
            className="mobile-menu-item mobile-menu-logout"
          >
            Logout
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
